import { useState } from 'react';
import {
	Table,
	Tag,
	Switch,
	Button,
	Modal,
	Form,
	Input,
	InputNumber,
	Select,
	Tooltip,
	Space,
	Spin,
	Popconfirm,
	notification,
} from 'antd';
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	StarFilled,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
	useGetAdminDekitAreasQuery,
	useGetKitMachineGroupsQuery,
	useCreateDekitAreaMutation,
	useUpdateDekitAreaMutation,
	useDeleteDekitAreaMutation,
} from '../../../redux/api/kit-staging';
import type { KtDekitArea, KitMachineGroup } from '../../../redux/api/kit-staging';

interface DekitAreaFormValues {
	name: string;
	description: string;
	route_order: number;
	is_default_fallback: boolean;
	machine_group_ids: number[];
}

const DekitAreasTab = () => {
	const { data: areas, isLoading } = useGetAdminDekitAreasQuery();
	const { data: machineGroups = [] } = useGetKitMachineGroupsQuery();
	const [createArea, { isLoading: creating }] = useCreateDekitAreaMutation();
	const [updateArea, { isLoading: updating }] = useUpdateDekitAreaMutation();
	const [deleteArea] = useDeleteDekitAreaMutation();

	const [modalOpen, setModalOpen] = useState(false);
	const [editingArea, setEditingArea] = useState<KtDekitArea | null>(null);
	const [form] = Form.useForm<DekitAreaFormValues>();

	const openCreateModal = () => {
		setEditingArea(null);
		form.resetFields();
		form.setFieldsValue({ route_order: 0, is_default_fallback: false });
		setModalOpen(true);
	};

	const openEditModal = (area: KtDekitArea) => {
		setEditingArea(area);
		form.setFieldsValue({
			name: area.name,
			description: area.description,
			route_order: area.route_order ?? 0,
			is_default_fallback: area.is_default_fallback ?? false,
			machine_group_ids: area.machine_groups?.map((mg) => mg.id) || [],
		});
		setModalOpen(true);
	};

	const closeModal = () => {
		setModalOpen(false);
		setEditingArea(null);
		form.resetFields();
	};

	const handleToggleActive = async (area: KtDekitArea, checked: boolean) => {
		try {
			await updateArea({ id: area.id, body: { is_active: checked } }).unwrap();
			notification.success({
				message: `${area.name} ${checked ? 'activated' : 'deactivated'}`,
			});
		} catch {
			notification.error({ message: `Failed to update ${area.name}` });
		}
	};

	const handleSubmit = async (values: DekitAreaFormValues) => {
		try {
			const payload = {
				name: values.name,
				description: values.description,
				route_order: values.route_order ?? 0,
				is_default_fallback: values.is_default_fallback ?? false,
				machine_group_ids: values.machine_group_ids || [],
			};
			if (editingArea) {
				await updateArea({ id: editingArea.id, body: payload }).unwrap();
				notification.success({ message: `Updated ${values.name}` });
			} else {
				await createArea(payload).unwrap();
				notification.success({ message: `Created ${values.name}` });
			}
			closeModal();
		} catch {
			notification.error({
				message: editingArea
					? 'Failed to update dekit area'
					: 'Failed to create dekit area',
			});
		}
	};

	const handleDelete = async (area: KtDekitArea) => {
		try {
			await deleteArea(area.id).unwrap();
			notification.success({ message: `Deleted ${area.name}` });
		} catch {
			notification.error({ message: `Failed to delete ${area.name}` });
		}
	};

	const columns: ColumnsType<KtDekitArea> = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (name: string, record) => (
				<Space>
					{name}
					{record.is_default_fallback && (
						<Tooltip title="Default fallback area">
							<StarFilled style={{ color: '#faad14', fontSize: 14 }} />
						</Tooltip>
					)}
				</Space>
			),
		},
		{
			title: 'Machine Groups',
			dataIndex: 'machine_groups',
			key: 'machine_groups',
			render: (groups?: KitMachineGroup[]) =>
				groups && groups.length > 0 ? (
					groups.map((mg) => (
						<Tag key={mg.id} color={mg.color}>
							{mg.name}
						</Tag>
					))
				) : (
					<span className="text-gray-400">All (no filter)</span>
				),
		},
		{
			title: 'Route Order',
			dataIndex: 'route_order',
			key: 'route_order',
			width: 100,
			align: 'center',
			sorter: (a, b) => (a.route_order ?? 0) - (b.route_order ?? 0),
		},
		{
			title: 'Description',
			dataIndex: 'description',
			key: 'description',
			render: (val: string) => val || '—',
		},
		{
			title: 'Active',
			dataIndex: 'is_active',
			key: 'is_active',
			width: 80,
			align: 'center',
			render: (active: boolean, record) => (
				<Tooltip
					title={
						active ? 'Deactivate this dekit area' : 'Activate this dekit area'
					}
				>
					<Switch
						checked={active}
						onChange={(checked) => handleToggleActive(record, checked)}
						onClick={(_, e) => e.stopPropagation()}
					/>
				</Tooltip>
			),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 100,
			align: 'center',
			render: (_, record) => (
				<Space>
					<Tooltip title="Edit">
						<Button
							type="text"
							icon={<EditOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								openEditModal(record);
							}}
						/>
					</Tooltip>
					<Popconfirm
						title={`Delete "${record.name}"?`}
						description="This action cannot be undone."
						onConfirm={() => handleDelete(record)}
						onPopupClick={(e) => e.stopPropagation()}
						okText="Delete"
						okType="danger"
						cancelText="Cancel"
					>
						<Tooltip title="Delete">
							<Button
								type="text"
								danger
								icon={<DeleteOutlined />}
								onClick={(e) => e.stopPropagation()}
							/>
						</Tooltip>
					</Popconfirm>
				</Space>
			),
		},
	];

	if (isLoading) {
		return (
			<div className="flex justify-center items-center py-24">
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="py-4">
			<div className="flex justify-end mb-4">
				<Tooltip title="Create a new dekit area">
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={openCreateModal}
					>
						Add Dekit Area
					</Button>
				</Tooltip>
			</div>

			<Table
				columns={columns}
				dataSource={areas || []}
				rowKey="id"
				pagination={false}
				size="middle"
			/>

			<Modal
				title={editingArea ? 'Edit Dekit Area' : 'Add Dekit Area'}
				open={modalOpen}
				onCancel={closeModal}
				footer={null}
			>
				<Form<DekitAreaFormValues>
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					initialValues={{ route_order: 0, is_default_fallback: false }}
				>
					<Form.Item
						name="name"
						label="Name"
						rules={[{ required: true, message: 'Please enter a name' }]}
					>
						<Input placeholder="e.g. Cell 1 Dekit" />
					</Form.Item>
					<Form.Item name="description" label="Description">
						<Input.TextArea rows={2} placeholder="Optional description" />
					</Form.Item>
					<Form.Item name="machine_group_ids" label="Machine Groups">
						<Select
							mode="multiple"
							placeholder="Select machine groups (leave empty for all)"
							optionLabelProp="label"
						>
							{machineGroups.map((mg: KitMachineGroup) => (
								<Select.Option key={mg.id} value={mg.id} label={mg.name}>
									<Tag color={mg.color}>{mg.name}</Tag>
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item
						name="route_order"
						label="Route Order"
						tooltip="Physical shop-floor sequence for cart route. Lower numbers are visited first."
					>
						<InputNumber min={0} style={{ width: '100%' }} />
					</Form.Item>
					<Form.Item
						name="is_default_fallback"
						label="Default Fallback"
						tooltip="If enabled, jobs with no mapped machine group will be sent here."
						valuePropName="checked"
					>
						<Switch />
					</Form.Item>
					<Form.Item className="mb-0 flex justify-end">
						<Space>
							<Button onClick={closeModal}>Cancel</Button>
							<Button
								type="primary"
								htmlType="submit"
								loading={editingArea ? updating : creating}
							>
								{editingArea ? 'Save' : 'Create'}
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default DekitAreasTab;
